import RedirectLink, { IRedirectLinkProps } from 'app/components/auth/RedirectLink';
import { tFunctionType, useI18n } from 'app/components/localization';
import { LogoutRoute } from 'app/constants/Routes';
import { BookingInterfaceConsumer, IBookingInterface } from 'app/containers/rtc/BookingContext';
import { BookingStateConsumer } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingState } from 'app/models/rtc/booking/constants';
import { getLogoutWarningMessage } from 'app/models/rtc/booking/warning';
import { confirm } from 'app/utils/env';
import React, { FunctionComponent, PureComponent } from 'react';
import { useIsAuthRestricted } from './RestrictPage';

interface IDeps {
  inRestrictedPage: boolean;
  bookingInterface: IBookingInterface;
  bookingState?: BookingState;
  t: tFunctionType;
}

type Props = Omit<IRedirectLinkProps, 'to' | 'onClick'>;

export class LogoutLinkBase extends PureComponent<Props & IDeps> {
  public render() {
    const { children, bookingState, inRestrictedPage, t, ...rest } = this.props;

    const to = LogoutRoute.getPathname();

    const noRedirectConditions = [
      () => inRestrictedPage,
      () => Boolean(bookingState),
    ];

    return (
      <RedirectLink
        to={to}
        noRedirectConditions={noRedirectConditions}
        onClick={this.getConfirmLogout}
        replace
        {...rest}
      >
        {children || t('admin.header.logout-link')}
      </RedirectLink>
    );
  }

  private getConfirmLogout = (e: React.MouseEvent) => {
    const { bookingInterface, bookingState, t } = this.props;

    if (bookingState) {
      const message = getLogoutWarningMessage(t, bookingState.bookingType);

      if (confirm(message)) {
        // Clear the active booking to ensure sync navigation does not cause
        // redirect events during logout.
        bookingInterface.clearActiveBooking();
      } else {
        e.preventDefault();
      }
    }
  };
}

const LogoutLinkInjectDependencies: FunctionComponent<Props> = (props) => {
  const { t } = useI18n();
  const inRestrictedPage = useIsAuthRestricted();
  return (
    <BookingInterfaceConsumer>{(bookingInterface) => (
      <BookingStateConsumer>{(bookingState) =>
        <LogoutLinkBase
          bookingInterface={bookingInterface}
          bookingState={bookingState}
          inRestrictedPage={Boolean(inRestrictedPage)}
          t={t}
          {...props}
        />
      }</BookingStateConsumer>
    )}</BookingInterfaceConsumer>
  );
};

export default LogoutLinkInjectDependencies;
